import {
  isColumnPreviewBlocked,
  PrivacyLevel,
} from "@hightouch/lib/query/privacy-utils";

// Server-side placeholder substituted for preview-masked column values
// (see REDACTED_PREVIEW_VALUE in backend sources/query.ts).
export const REDACTED_PREVIEW_VALUE = "<REDACTED BY HIGHTOUCH>";

/**
 * Columns whose values are kept in preview rows but replaced with a
 * redaction placeholder. Narrower than {@link getPreviewMaskedColumnNames}:
 * access-blocked columns (disable / blocked / syncOnly) are stripped from
 * preview rows entirely rather than redacted.
 */
export const isColumnPreviewMasked = (column: {
  disable_preview: boolean;
  privacy_level?: string | null;
}): boolean =>
  column.disable_preview || column.privacy_level === PrivacyLevel.REDACTED;

export const isRedactedPreviewValue = (value: unknown): boolean =>
  value === REDACTED_PREVIEW_VALUE;

type ColumnPreviewMeta = {
  name: string;
  alias: string | null;
  disable?: boolean;
  disable_preview: boolean;
  privacy_level?: string | null;
};

/**
 * Display names (alias when set, otherwise raw name) of columns whose values
 * are hidden in preview query results — redacted to a placeholder or stripped
 * entirely — so they can't be displayed to users.
 */
export const getPreviewMaskedColumnNames = (
  columns: ColumnPreviewMeta[],
): Set<string> =>
  new Set(
    columns
      .filter((column) =>
        isColumnPreviewBlocked({
          disable: column.disable,
          disablePreview: column.disable_preview,
          privacyLevel: column.privacy_level ?? null,
        }),
      )
      .map((column) => column.alias || column.name),
  );

type PreviewRowLabelArgs = {
  row: Record<string, unknown>;
  primaryLabel: string;
  secondaryLabel: string;
  primaryKey: string;
  maskedColumns: ReadonlySet<string>;
};

/**
 * Label for a preview row: the first of primary label, secondary label, and
 * primary key whose value is present and not hidden by data masking. Falls
 * back to the primary key so a row always has a stable, unique label.
 */
export const getPreviewRowLabel = ({
  row,
  primaryLabel,
  secondaryLabel,
  primaryKey,
  maskedColumns,
}: PreviewRowLabelArgs): string => {
  for (const column of [primaryLabel, secondaryLabel, primaryKey]) {
    if (!column || maskedColumns.has(column)) {
      continue;
    }
    const value = row[column];
    if (value != null && value !== "" && !isRedactedPreviewValue(value)) {
      return String(value);
    }
  }
  return String(row[primaryKey] ?? "");
};

/**
 * Secondary-label description for a preview row; empty when the column is
 * masked, the value is redacted or absent, or it would just repeat the label
 * (the label falls back to the secondary label when the primary is masked).
 */
export const getPreviewRowDescription = ({
  row,
  primaryLabel,
  secondaryLabel,
  primaryKey,
  maskedColumns,
}: PreviewRowLabelArgs): string => {
  if (!secondaryLabel || maskedColumns.has(secondaryLabel)) {
    return "";
  }
  const description = row[secondaryLabel];
  if (description == null || isRedactedPreviewValue(description)) {
    return "";
  }
  const label = getPreviewRowLabel({
    row,
    primaryLabel,
    secondaryLabel,
    primaryKey,
    maskedColumns,
  });
  return String(description) === label ? "" : String(description);
};
